import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import apiClient from "../api/apiClient";
import ENDPOINTS from "../api/endPoint";
import { getToken } from "../utils/tokenUtils";

// Function to check if token is expired
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true; // No token, force login

  try {
    const decoded = jwtDecode(token);
    const currentTimestamp = Math.floor(Date.now() / 1000); // Convert to seconds
    return decoded.exp < currentTimestamp + 120; // Refresh 1 min before expiry
  } catch (error) {
    return true; // Token is invalid
  }
};

// Function to refresh token
export const refreshAccessToken = async () => {
  const oldAccessToken = Cookies.get("accessTokenJKT");
  const oldRefreshToken = Cookies.get("refreshTokenJKT");

  if (!oldRefreshToken) {
    console.error("No refresh token found!");
    return null;
  }

  try {
    const data = await apiClient(ENDPOINTS?.REFRESH, {
      method: "POST",
      body: { accessToken: oldAccessToken, refreshToken: oldRefreshToken },
      requiresAuth: false, // Refresh API doesn't require token
    });

    const { accessToken, refreshToken } = data;
    const decoded = jwtDecode(accessToken);
    Cookies.set(
      "role",
      decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]
    ); // 1 day expiry

    // Store tokens securely in cookies
    Cookies.set("accessTokenJKT", accessToken, {
      secure: true,
      sameSite: "Strict",
    });
    Cookies.set("refreshTokenJKT", refreshToken, {
      secure: true,
      sameSite: "Strict",
    });

    return accessToken;
  } catch (error) {
    console.error("Failed to refresh token. Logging out...");
    Cookies.remove("accessTokenJKT");
    Cookies.remove("refreshTokenJKT");
    window.location.href = "/";
    throw new Error("Session expired. Please log in again.");
  }
};

export const loginUser = async (email, password) => {
  const data = await apiClient(ENDPOINTS?.LOGIN, {
    method: "POST",
    body: { email, password },
    requiresAuth: false,
  });

  const { accessToken, refreshToken } = data;
  const decoded = jwtDecode(accessToken);

  // Store tokens securely in cookies
  Cookies.set("accessTokenJKT", accessToken, {
    secure: true,
    sameSite: "Strict",
  });
  Cookies.set("refreshTokenJKT", refreshToken, {
    secure: true,
    sameSite: "Strict",
  });
  Cookies.set(
    "role",
    decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]
  ); // Store role in cookies

  return {
    accessToken,
    refreshToken,
    role: decoded[
      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
    ],
  };
};

// Fetch current user info
export const fetchUserInfo = (id) => {
  return apiClient(ENDPOINTS.USER_GET_BY_ID(id), {
    method: "GET",
  });
};

// Function to logout user
export const logoutUser = async (id) => {
  try {
    // Await the backend logout call to ensure it actually fires
    await apiClient(ENDPOINTS.LOGOUT(id), {
      method: "POST",
    });
  } catch (err) {
    console.error("Logout API failed:", err);
  } finally {
    // Clear authentication tokens and local state
    Cookies.remove("accessTokenJKT");
    Cookies.remove("refreshTokenJKT");
    Cookies.remove("role");

    // Redirect
    window.location.href = "/";
  }
};

export const createPassword = (payload) => {
  return apiClient(ENDPOINTS.CREATE_PASSWORD, {
    method: "POST",
    body: payload,
  });
};

// authService.js
export const initiateForgotPasswordSvs = async (email) => {
  return await apiClient(ENDPOINTS.FORGOT_PASSWORD_SEND_OTP, {
    method: "POST",
    body: { username: email },
    requiresAuth: false,
  });
};

export const verifyPasswordResetOtp = async ({ username, otp }) => {
  return await apiClient(ENDPOINTS.FORGOT_PASSWORD_VERIFY_OTP, {
    method: "POST",
    body: { username, otpCode: otp },
    requiresAuth: false,
  });
};

export const resetUserPassword = async ({
  username,
  newPassword,
  confirmPassword,
}) => {
  return await apiClient(ENDPOINTS.RESET_PASSWORD, {
    method: "POST",
    body: { userName: username, newPassword, confirmPassword },
    requiresAuth: false,
  });
};
